import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { AlertCircle, Loader2, Sparkles } from "lucide-react";
import { type DocumentDetailsRelease, type GeneratedQuiz } from "~/api/types";
import { generateQuiz } from "~/api/v2/requests";
import { Alert, AlertTitle } from "~/atoms/alert";
import { Button } from "~/atoms/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/atoms/card";
import { featureFlags } from "~/config/featureFlags";
import { isPublishedArtifact } from "~/helpers/documentPermissions";
import { QuizTaker } from "./QuizTaker";

interface Props {
  release: DocumentDetailsRelease;
}

type QuizState =
  | { status: "idle" | "generating" | "failed" }
  | { status: "ready" | "taking" | "done"; quiz: GeneratedQuiz };

/**
 * "Take quiz" entry point on the anchoring tab. The first press generates a
 * quiz for the page visit; later presses open the same quiz. Nothing is stored.
 */
export const QuizSection = ({ release }: Props) => {
  const { t } = useTranslation();
  const isQuizEnabled = useFeatureFlagEnabled(featureFlags.quiz);
  const [state, setState] = useState<QuizState>({ status: "idle" });

  const canTakeQuiz =
    isQuizEnabled &&
    isPublishedArtifact(release) &&
    release.artifact.fileType === "docx";
  if (!canTakeQuiz) {
    return null;
  }

  const generate = async () => {
    setState({ status: "generating" });
    try {
      const quiz = await generateQuiz(release.id);
      setState({ status: "ready", quiz });
    } catch (error) {
      console.error("Failed to generate quiz:", error);
      setState({ status: "failed" });
    }
  };

  const startTaking = (quiz: GeneratedQuiz) =>
    setState({ status: "taking", quiz });

  if (state.status === "taking") {
    return (
      <QuizTaker
        questions={state.quiz.questions}
        onDone={() => setState({ status: "done", quiz: state.quiz })}
      />
    );
  }

  if (state.status === "done") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t(($) => $.quiz.doneTitle)}</CardTitle>
          <CardDescription>{t(($) => $.quiz.doneDescription)}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={() => startTaking(state.quiz)}>
            {t(($) => $.quiz.takeAgain)}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const isGenerating = state.status === "generating";

  return (
    <div className="flex flex-col gap-2">
      {state.status === "failed" && (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertTitle>{t(($) => $.quiz.generationFailed)}</AlertTitle>
        </Alert>
      )}
      {state.status === "ready" && (
        <p className="text-muted-foreground">{t(($) => $.quiz.ready)}</p>
      )}
      <Button
        onClick={() =>
          state.status === "ready" ? startTaking(state.quiz) : void generate()
        }
        disabled={isGenerating}
        className="ai-gradient-button text-primary-foreground relative w-fit overflow-hidden font-semibold"
      >
        {isGenerating ? (
          <Loader2 data-icon="inline-start" className="animate-spin" />
        ) : (
          <Sparkles data-icon="inline-start" />
        )}
        {isGenerating ? t(($) => $.quiz.generating) : t(($) => $.quiz.takeQuiz)}
      </Button>
    </div>
  );
};
